/**
 * DataLayer Service
 * Core service for pushing events to the dataLayer.
 * Framework-agnostic implementation that works with Angular, AngularJS, and vanilla JS/TS.
 */

import {BaseEvent, FormInteractionType} from './types/common.js';
import {
    FormStart,
    FormInteraction,
    FormSuccess,
} from './types/form-events.js';
import { PageView } from './types/page-events.js';
import { ButtonClick } from './types/button-events.js';
import {
    buildFormStartEvent,
    buildFormInteractionEvent,
    buildFormSuccessEvent,
} from './events/form-event-builder.js';
import { buildPageViewEvent } from './events/page-event-builder.js';
import { buildButtonClickEvent } from './events/button-click-event-builder.js';
import { AbTestVariantProvider, setAbTestVariantProvider } from './events/base-event-builder.js';
import { addStickyKeys, withStaleKeysCleared } from './key-scope.js';

// Extend Window interface for dataLayer
declare global {
    interface Window {
        dataLayer: unknown[];
        DataLayerService: typeof DataLayerService;
    }
}

/**
 * DataLayerService
 *
 * A static service class that provides methods to push tracking events
 * to the Google Tag Manager dataLayer.
 *
 * @example
 * // Basic usage
 * DataLayerService.sendFormStartEvent({
 *     formName: FormName.ConsumerQuoteRequest,
 *     formId: 'abc123'
 * });
 *
 * @example
 * // Enable debugging
 * // Visit page with #enable-ga-debug hash, or run:
 * // localStorage.setItem('ga-debug', '1');
 *
 * @example
 * // Page view with auto-detected AB test variant
 * DataLayerService.sendPageViewEvent({
 *     audienceType: 'consumer',
 *     pageType: 'form',
 *     pageTemplate: 'quote_request',
 *     locale: 'en_GB',
 *     businessUnit: 'MOVE',
 *     brandId: 1,
 *     brandName: 'SkyDreams'
 * });
 */
export class DataLayerService {
    /**
     * Get or initialize the dataLayer array
     */
    private static getDataLayer(): unknown[] {
        if (typeof window === 'undefined') {
            console.warn('DataLayerService: window is not defined');
            return [];
        }

        window.dataLayer = window.dataLayer || [];
        return window.dataLayer;
    }

    /**
     * Push any event to the dataLayer.
     *
     * Non-sticky keys an earlier event left in GTM's cumulative model are cleared
     * here, so an event never reports a value it did not set. See key-scope.ts.
     */
    public static push(event: unknown): void {
        const payload = withStaleKeysCleared(event);
        const dataLayer = this.getDataLayer();
        dataLayer.push(payload);
        this.debugEvent(payload);
    }

    /**
     * Push a typed event to the dataLayer
     */
    public static pushEvent<T extends BaseEvent>(event: T): void {
        this.push(event);
    }

    /**
     * Send a form_start event
     *
     * @example
     * DataLayerService.sendFormStartEvent({
     *     formName: FormName.ConsumerQuoteRequest,
     *     formId: document.querySelector('[data-authcode]')?.getAttribute('data-authcode')
     * });
     */
    public static sendFormStartEvent<T extends FormStart>(options: T): void {
        const event = buildFormStartEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a form_start event with Promise-based callback
     * Useful when you need to wait for GTM to process the event before proceeding
     *
     * @param options Form start options
     * @param timeoutMs Timeout in milliseconds (default: 500ms)
     * @returns Promise that resolves when GTM fires callback or times out
     */
    public static sendFormStartEventAsync<T extends FormStart>(
        options: T,
        timeoutMs: number = 500
    ): Promise<void> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                console.warn('DataLayerService: GTM callback did not fire in time');
                resolve();
            }, timeoutMs);

            const event = buildFormStartEvent(options);
            const eventWithCallback = {
                ...event,
                eventCallback: () => {
                    clearTimeout(timeout);
                    resolve();
                },
            };

            this.push(eventWithCallback);
        });
    }

    /**
     * Send a form_interaction event
     *
     * @example
     * DataLayerService.sendFormInteractionEvent({
     *     formName: FormName.ConsumerQuoteRequest,
     *     formInteraction: 'view',
     *     formStepName: 'address_details',
     *     formStep: 1,
     *     formTotalSteps: 3,
     *     formPhaseName: 'address',
     *     formPhase: 1,
     *     formTotalPhases: 3
     * });
     */
    public static sendFormInteractionEvent<T extends FormInteraction>(options: T): void {
        // Validate form_interaction type
        if (!Object.values(FormInteractionType).includes(options.formInteraction as FormInteractionType)) {
            console.warn(`DataLayerService: Invalid form interaction type '${options.formInteraction}'. Allowed types are: ${Object.values(FormInteractionType).join(', ')}`);
            return;
        }

        const event = buildFormInteractionEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a form_interaction event with error data
     * @deprecated Use sendFormInteractionEvent with formInteraction: FormInteractionType.ERROR and errorData property
     */
    public static sendFormErrorEvent<T extends FormInteraction>(options: T): void {
        this.sendFormInteractionEvent(options);
    }

    /**
     * Send a form_success event
     *
     * @example
     * DataLayerService.sendFormSuccessEvent({
     *     formName: FormName.ConsumerQuoteRequest,
     *     formId: 'abc123',
     *     formVersionId: 'v1',
     *     emailHash: 'sha256hash...',
     *     businessUnit: 'MOVE',
     *     company: {
     *         companyId: 123,
     *         companyName: 'Test Company',
     *         businessType: 'moving',
     *         contactEmail: 'test@example.com',
     *         contactTelephone: '+3112345678',
     *         productGroup: 'moving'
     *     },
     *     companyAddress: {
     *         countryIso: 'NL'
     *     },
     *     leadDetails: {
     *         targetingArea: {
     *             type: TargetingAreaType.Radius,
     *             unitSystem: 'metric',
     *             radiusValue: 50
     *         }
     *     }
     * });
     */
    public static sendFormSuccessEvent<T extends FormSuccess>(options: T): void {
        const event = buildFormSuccessEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a page view event
     * If abTestVariant is not provided, resolves it via the app-registered provider
     *
     * @example
     * DataLayerService.sendPageViewEvent({
     *     audienceType: 'consumer',
     *     pageType: 'category',
     *     pageTemplate: 'moving',
     *     locale: 'en_GB',
     *     businessUnit: 'MOVE',
     *     brandId: 1,
     *     brandName: 'SkyDreams',
     *     categoryId: 5,
     *     // categoryName is not in PageViewOptions, check types if needed
     * });
     */
    public static sendPageViewEvent<T extends PageView>(options: T): void {
        const event = buildPageViewEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a button_click event
     *
     * @example
     * DataLayerService.sendButtonClickEvent({
     *     elementId: 'hero-cta',
     *     interactionType: 'click',
     * });
     */
    public static sendButtonClickEvent<T extends ButtonClick>(options: T): void {
        const event = buildButtonClickEvent(options);
        this.pushEvent(event);
    }

    /**
     * Send a custom event with arbitrary data
     * Use this for events that don't fit the standard patterns
     */
    public static sendCustomEvent(eventName: string, data: Record<string, unknown> = {}): void {
        this.push({
            event: eventName,
            ...data,
        });
    }

    /**
     * Log event to the console for debugging purposes.
     * Only logs when localStorage['ga-debug'] is set (set it directly, or visit
     * any page with the #enable-ga-debug hash), so debugging works in every
     * environment, not just where the app renders a debug marker.
     */
    private static debugEvent(event: unknown): void {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            if (!window.localStorage.getItem('ga-debug')) {
                return;
            }

            // eslint-disable-next-line no-console
            console.info('GA event pushed:', JSON.stringify(event, null, 2));
        } catch {
            // Storage blocked (e.g. Safari private mode) or the event is not
            // serializable — debug logging must never break the push itself
        }
    }

    /**
     * Registers the app's source for the auto-resolved ab_test_variant — the
     * package has no default of its own. Register once at app bootstrap,
     * e.g. () => document.body.getAttribute('data-ab'); also reachable for
     * non-module consumers via window.DataLayerService.setAbTestVariantProvider(...).
     */
    public static setAbTestVariantProvider(provider: AbTestVariantProvider): void {
        setAbTestVariantProvider(provider);
    }

    /**
     * Marks app-specific keys (snake_case wire names) as page-scoped, so they are
     * inherited by later events instead of being cleared. Only for values that
     * stay true for every event on the page — see key-scope.ts.
     */
    public static addStickyKeys(keys: readonly string[]): void {
        addStickyKeys(keys);
    }

    /**
     * Initialize debug mode from the URL hash.
     * Call this on page load to enable debugging when #enable-ga-debug is present.
     */
    public static initDebugMode(): void {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            if (window.location.hash === '#enable-ga-debug') {
                window.localStorage.setItem('ga-debug', '1');
                // eslint-disable-next-line no-console
                console.info('GA debug enabled');
            }
        } catch {
            // Storage blocked in this context (e.g. Safari with cookies disabled, private mode)
        }
    }
}

// Expose on window for non-module contexts (AngularJS, legacy scripts)
if (typeof window !== 'undefined') {
    // todo: migrate this to skyComponents namespace
    window.DataLayerService = DataLayerService;

    // Auto-initialize debug mode from URL hash
    DataLayerService.initDebugMode();
}

